import React from 'react'
import { Field } from 'formik'
import { useIntl, IntlShape } from 'react-intl'
import { Form } from 'lib/ui'
import { MagentoCustomizableOptionInterface } from 'lib/types'
import { DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import { getOptionName } from '../../schema'
import { Renderer } from '../..'

interface Props {
  option: MagentoCustomizableOptionInterface
  renderer?: Renderer
}

export const Option = ({ option, renderer }: Props) => {
  const intl = useIntl()

  const {
    title,
    area_value: areaValue,
    checkbox_value: checkboxValue,
    dropdown_value: dropdownValue,
    field_value: fieldValue,
    radio_value: radioValue,
    required
  } = option

  let label = title
  const name = getOptionName(option)

  if (fieldValue) {
    const { max_characters: maxLength } = fieldValue
    const { price } = fieldValue
    if (price) {
      label = `${label} (${getFormattedPrice({ price, intl })})`
    }

    return (
      <Field
        name={name}
        label={label}
        component={Form.Input}
        maxLength={maxLength}
        isRequired={required}
      />
    )
  }

  if (areaValue) {
    const { max_characters: maxLength } = areaValue
    const { price } = areaValue
    if (price) {
      label = `${label} (${getFormattedPrice({ price, intl })})`
    }

    return (
      <Field
        name={name}
        label={label}
        component={Form.TextArea}
        maxLength={maxLength}
        isRequired={required}
      />
    )
  }

  if (dropdownValue) {
    if (renderer?.DropDown) {
      const options = dropdownValue.map((el) => {
        const formattedPrice = getFormattedPrice({ price: el.price, intl })
        return {
          key: `${option.option_id}-${el.option_type_id}`,
          data: el,
          value: el.option_type_id.toString(),
          formattedPrice
        }
      })

      return renderer?.DropDown({
        label,
        name,
        options,
        isRequired: required
      })
    }

    const options = dropdownValue.map(el => {
      return {
        label: `${el.title} (${getFormattedPrice({ price: el.price, intl })})`,
        value: el.option_type_id.toString()
      }
    })

    options.unshift({ label: '', value: '' })

    return (
      <Field
        name={name}
        label={label}
        options={options}
        component={Form.Select}
        isRequired={required}
      />
    )
  }

  if (checkboxValue) {
    if (renderer?.Checkbox) {
      const options = checkboxValue.map((el) => {
        const formattedPrice = getFormattedPrice({ price: el.price, intl })
        return {
          key: `${option.option_id}-${el.option_type_id}`,
          data: el,
          value: el.option_type_id.toString(),
          formattedPrice
        }
      })

      return renderer?.Checkbox({
        label,
        name,
        options,
        isRequired: required
      })
    }

    return (
      <div role='group' aria-labelledby='checkbox-group'>
        <p>{label}</p>
        {checkboxValue.map((el) => {
          const label = `${el.title} (${getFormattedPrice({ price: el.price, intl })})`
          const value = el.option_type_id.toString()
          return (
            <div key={`${option.option_id}-${el.option_type_id}`}>
              <label>
                <Field type='checkbox' name={name} value={value} />
                <span>{label}</span>
              </label>
            </div>
          )
        })}
      </div>
    )
  }

  if (radioValue) {
    if (renderer?.Radio) {
      const options = radioValue.map((el) => {
        const formattedPrice = getFormattedPrice({ price: el.price, intl })
        return {
          key: `${option.option_id}-${el.option_type_id}`,
          data: el,
          value: el.option_type_id.toString(),
          formattedPrice
        }
      })

      return renderer?.Radio({
        label,
        name,
        options,
        isRequired: required
      })
    }

    return (
      <div role='group' aria-labelledby='radio-group'>
        <p>{label}</p>
        {radioValue.map((el) => {
          const label = `${el.title} (${getFormattedPrice({ price: el.price, intl })})`
          const value = el.option_type_id.toString()
          return (
            <div key={`${option.option_id}-${el.option_type_id}`}>
              <label>
                <Field type='radio' name={name} value={value} />
                <span>{label}</span>
              </label>
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

const getFormattedPrice = ({ price, intl }: { price: number, intl: IntlShape }) => {
  return intl.formatNumber(price, { style: 'currency', currency: DEFAULT_CURRENCY_CODE })
}
